import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Add } from '@factorialco/f0-react/icons/app'
import { runInAction } from 'mobx'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'
import FormEditor from 'modules/forms/components/FormEditor'
import { elementFromData } from 'modules/forms/hooks/useForms'
import { Form } from 'modules/forms/lib/Form'
import { ContentViewProps } from 'modules/trainings/components/content/types'
import { QuizBlock } from 'modules/trainings/lib/content/QuizBlock'

const initializeFormFromQuiz = (quiz: QuizBlock): Form | undefined => {
  quiz.title = quiz.title || ''

  if (!quiz.form) {
    return undefined
  }

  const configuration = {
    allowedElements: quiz.form.configuration?.allowedElements || [],
    retryable: quiz.form.configuration?.retryable ?? true,
    scorable: quiz.form.configuration?.scorable ?? true,
  }

  const formInstance = new Form(
    quiz.form.id || '',
    quiz.form.title,
    quiz.form.description || null,
    quiz.form.resourceType,
    quiz.form.resourceId || null,
    'quiz',
    'draft',
    false,
    [],
    {},
    configuration
  )

  quiz.form.elements.forEach((elementData) => {
    const element = elementFromData({
      id: elementData.id || undefined,
      label: elementData.label,
      description: elementData.description || '',
      mandatory: elementData.mandatory,
      options:
        elementData.options?.map((opt) => ({
          id: opt.id || undefined,
          label: opt.label,
          value: opt.value,
          isCorrect: opt.isCorrect || false,
        })) || [],
      params: {},
      position: elementData.position,
      sectionId: undefined,
      type: elementData.type,
      hasCorrectAnswer: elementData.hasCorrectAnswer || false,
    })
    formInstance.addElement(element)
  })

  return formInstance
}

const QuizBlockView = observer(({ contentItem: quiz }: ContentViewProps<QuizBlock>) => {
  const [form, setForm] = useState<Form | undefined>(() => initializeFormFromQuiz(quiz))
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false)

  const handleFormChange = (updatedForm: Form) => {
    setForm(updatedForm)

    if (quiz.form) {
      runInAction(() => {
        if (quiz.form) {
          quiz.form.title = updatedForm.title
          quiz.form.description = updatedForm.description
          quiz.form.elements = updatedForm.elements.map((element) => ({
            id: element.id || null,
            label: element.label,
            description: element.description || null,
            type: element.getType(),
            position: element.position,
            mandatory: element.mandatory,
            hasCorrectAnswer: element.hasCorrectAnswer,
            options: element.options.map((option) => ({
              id: option.id || null,
              label: option.label,
              value: option.value,
              isCorrect: option.isCorrect,
            })),
          }))
        }
      })
    }
  }

  if (!form) {
    return null
  }

  return (
    <Box width="full" paddingX="s24">
      <Box paddingX="s16" gap="s8">
        <Text.Header1 weight="bold">{quiz.title}</Text.Header1>
        <Text.Normal size="200" color="textSecondary">
          {quiz.form?.description}
        </Text.Normal>
      </Box>
      <Box flexDirection="row" gap="s8" justifyContent="flexEnd" paddingX="s16">
        <F0Button
          onClick={() => setShowAddQuestionModal(true)}
          icon={Add}
          variant="outline"
          label={i18n.t('trainings.content.block.quiz.add_question')}
        />
      </Box>

      <FormEditor
        form={form}
        setForm={handleFormChange}
        hideSidebar
        showAllElements
        showAddQuestionModal={showAddQuestionModal}
        onCloseAddQuestionModal={() => setShowAddQuestionModal(false)}
        permittedFieldTypes={quiz.form?.configuration?.allowedElements || ['select', 'multiselect']}
      />
    </Box>
  )
})

export default QuizBlockView
